/* DLUXCA Quick Order v3 — manual entry first. Requires Invoice Worker v4 and the product data template. */
(() => {
  'use strict';
  const LIMIT = 200;
  const clean = value => String(value == null ? '' : value).trim();

  function parseDelimited(text, separator) {
    text = text.replace(/^\uFEFF/, '');
    const rows = [];
    let row = [],
      field = '',
      quoted = false,
      closed = false;
    const pushField = () => {
      row.push(field);
      field = '';
      closed = false;
    };
    const pushRow = () => {
      pushField();
      if (row.some(v => v.trim())) rows.push(row);
      row = [];
    };
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (quoted) {
        if (c === '"' && text[i + 1] === '"') {
          field += '"';
          i++;
        } else if (c === '"') {
          quoted = false;
          closed = true;
        } else field += c;
      } else if (c === separator) pushField();
      else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        pushRow();
      } else if (c === '"' && !field && !closed) quoted = true;
      else if (closed && c !== ' ' && c !== '\t') throw new Error(
        'Invalid CSV quoting. Use two columns: SKU and Quantity.');
      else if (!closed) field += c;
      if (rows.length > LIMIT + 1) throw new Error('Maximum 200 items per upload.');
    }
    if (quoted) throw new Error('A quoted cell is incomplete. Please check your file.');
    if (field || row.length || closed) pushRow();
    return rows;
  }

  function normalizeRows(matrix) {
    let rows = matrix.filter(r => r.some(c => clean(c)));
    if (clean(rows[0]?.[0]).toLowerCase() === 'sku') {
      if (!['quantity', 'qty'].includes(clean(rows[0][1]).toLowerCase())) throw new Error(
        'Use SKU and Quantity as the first two column headings.');
      rows = rows.slice(1);
    }
    if (!rows.length) throw new Error('Add at least one SKU and quantity.');
    if (rows.length > LIMIT) throw new Error('Maximum 200 items per upload.');
    if (rows.some(r => r.slice(2).some(v => clean(v)))) throw new Error(
      'Use only two columns: SKU and Quantity.');
    return rows.map(r => ({
      sku: clean(r[0]),
      quantity: clean(r[1]),
      status: 'unchecked',
      message: 'Not checked yet.',
      matches: [],
      selected: null
    }));
  }

  function validation(row) {
    if (!row.sku || row.sku.length > 200 || /[\u0000-\u001f\u007f]/.test(row.sku))
    return 'Enter a valid SKU (up to 200 characters).';
    if (!/^\d+$/.test(row.quantity) || !Number.isSafeInteger(Number(row.quantity)) || Number(row
        .quantity) < 1 || Number(row.quantity) > 99999)
    return 'Enter a whole quantity from 1 to 99999.';
    return null;
  }

  function parserWorker() {
    self.onmessage = e => {
      try {
        importScripts(e.data.parser);
        const book = XLSX.read(e.data.buffer, {
          type: 'array',
          sheetRows: 202,
          cellFormula: true,
          cellHTML: false,
          cellStyles: false
        });
        const name = book.SheetNames[0];
        if (!name) throw new Error('The workbook has no worksheets.');
        const sheet = book.Sheets[name];
        const range = XLSX.utils.decode_range(sheet['!fullref'] || sheet['!ref'] || 'A1');
        if (range.e.r > 200 || range.e.c > 20) throw new Error(
          'Use the template with at most 200 items and two columns. Remove unused rows and columns.'
          );
        for (let r = 0; r <= range.e.r; r++) {
          for (let c = 0; c <= Math.min(range.e.c, 20); c++) {
            const cell = sheet[XLSX.utils.encode_cell({
              r,
              c
            })];
            if (cell?.f) throw new Error(
              'Use values only, not formulas. Please paste your values into a new sheet.');
          }
        }
        const rows = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: '',
          blankrows: false
        });
        const numericSku = Object.keys(sheet).some(key => /^A\d+$/.test(key) && sheet[key]
          ?.t === 'n');
        self.postMessage({
          ok: true,
          rows,
          name,
          numericSku,
          multipleSheets: book.SheetNames.length > 1
        });
      } catch (error) {
        self.postMessage({
          ok: false,
          message: error.message || 'Could not read the Excel file.'
        });
      }
    };
  }
  class QuickOrder extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.rows = [this.blankRow()];
      this.busy = false;
      this.$ = s => this.querySelector(s);
      this.$('[data-paste]').addEventListener('input',e=>{if(e.target.value.trim())this.trackStarted();});
      this.$('[data-import]').onclick = () => this.importText();
      this.$('[data-file]').onchange = e => this.importFile(e.target.files[0]);
      this.$('[data-check]').onclick = () => this.check();
      this.$('[data-cancel]').onclick = () => this.controller?.abort();
      this.$('[data-clear]').onclick = () => {
        if (!this.busy && confirm('Clear this Quick Order list?')) {
          this.rows = [this.blankRow()];
          this.render();
          this.say('List cleared. Your shopping cart is unchanged.');
        }
      };
      this.$('[data-add-row]').onclick = () => {
        if (!this.busy && this.rows.length < LIMIT) {
          this.rows.push({
            sku: '',
            quantity: '1',
            status: 'unchecked',
            message: 'Not checked yet.',
            matches: [],
            selected: null
          });
          this.render();
        }
      };
      this.$('[data-template]').onclick = () => this.downloadTemplate();
      this.$('[data-select-all]').onchange = event => {
        this.rows.forEach(row => {
          row.checked = this.canSelect(row) && event.target.checked;
        });
        this.syncSelection();
      };
      this.$('[data-add-selected]').onclick = () => this.addSelected();
      this.$('[data-cart-link]').href = this.storeUrl('cart');
      this.render();
      this.onConsent = () => this.trackOpened();
      document.addEventListener('visitorConsentCollected',this.onConsent);
      document.addEventListener('visibilitychange',this.onConsent);
      this.trackOpened();
      let attempts=0;this.openTimer=setInterval(()=>{this.trackOpened();if(++attempts>=30)clearInterval(this.openTimer);},1000);
    }
    blankRow(){return {sku:'',quantity:'1',status:'unchecked',message:'Enter a SKU and quantity, then match your items.',matches:[],selected:null,checked:false};}
    trackUsage(feature){
      try{
        if(!this.isConnected||window.DluxcaUsageEnabled!==true||window.Shopify?.designMode||window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()!==true||typeof window.DluxcaUsage?.track!=='function')return false;
        window.DluxcaUsage.track(feature,'success');return true;
      }catch{return false;}
    }
    trackOpened(){
      if(document.visibilityState==='hidden'||window.__dluxcaQuickOrderOpenedV3)return;
      if(this.trackUsage('quick_order_open'))window.__dluxcaQuickOrderOpenedV3=true;
    }
    trackStarted(){
      if(window.__dluxcaQuickOrderStartedV3)return;
      this.trackOpened();
      if(this.trackUsage('quick_order_start'))window.__dluxcaQuickOrderStartedV3=true;
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.worker?.terminate();
      clearInterval(this.openTimer);
      document.removeEventListener('visitorConsentCollected',this.onConsent);
      document.removeEventListener('visibilitychange',this.onConsent);
    }
    say(text) {
      this.$('[data-status]').textContent = text;
    }
    setBusy(value) {
      this.busy = value;
      this.$('[data-entry]').disabled = value;
      this.querySelectorAll('button,input,select,textarea').forEach(el => {
        if (!el.matches('[data-cancel]')) el.disabled = value;
      });
      this.$('[data-cancel]').hidden = !value || !this.controller;
      this.setAttribute('aria-busy', String(value));
      this.syncSelection();
    }
    replaceRows(matrix, message) {
      const rows = normalizeRows(matrix);
      if (this.rows.some(r=>r.sku.trim()||r.cartState||r.quantity!=='1') && !confirm(
          'Replace the current Quick Order list? Your shopping cart will not change.')) return false;
      this.rows = rows;
      this.render();
      this.say(message + ' Click Match items to look up your products.');
      return true;
    }
    async importText() {
      if (this.busy) return;
      try {
        const text = this.$('[data-paste]').value;
        if (text.length > 50000) throw new Error('Paste up to 200 rows.');
        if(text.trim())this.trackStarted();
        if(this.replaceRows(parseDelimited(text, text.includes('\t') ? '\t' : ','), 'Pasted items loaded.'))await this.check();
      } catch (error) {
        this.say(error.message);
      }
    }
    async importFile(file) {
      if (!file || this.busy) return;
      this.trackStarted();
      this.setBusy(true);
      try {
        if (file.size > 2 * 1024 * 1024) throw new Error('Maximum file size is 2 MB.');
        const ext = file.name.split('.').pop().toLowerCase();
        if (ext === 'csv') {
          this.replaceRows(parseDelimited(await file.text(), ','), 'CSV loaded.');
        } else if (ext === 'xlsx') {
          this.say('Reading Excel file…');
          const buffer = await file.arrayBuffer();
          const data = await this.readExcel(buffer);
          this.replaceRows(data.rows, 'Loaded worksheet "' + data.name + '".' + (data
            .multipleSheets ? ' Other worksheets were not imported.' : '') + (data
            .numericSku ?
            ' Some SKU cells are numeric: verify any leading zeroes before checking.' : ''));
        } else throw new Error('Upload an .xlsx or .csv file.');
      } catch (error) {
        this.say(error.message);
      } finally {
        this.$('[data-file]').value = '';
        this.setBusy(false);
      }
    }
    readExcel(buffer) {
      return new Promise((resolve, reject) => {
        let worker, timer, blobUrl;
        const finish = (error, data) => {
          clearTimeout(timer);
          worker?.terminate();
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          this.worker = null;
          error ? reject(error) : resolve(data);
        };
        try {
          blobUrl = URL.createObjectURL(new Blob(['(' + parserWorker.toString() + ')();'], {
            type: 'text/javascript'
          }));
          worker = this.worker = new Worker(blobUrl);
          timer = setTimeout(() => finish(new Error(
            'Excel file took too long to read. Try a smaller file or paste from Excel.'
            )), 15000);
          worker.onmessage = e => e.data.ok ? finish(null, e.data) : finish(new Error(e.data
            .message));
          worker.onerror = () => finish(new Error(
            'Excel reader could not start. Try CSV or paste from Excel.'));
          worker.postMessage({
            parser: new URL(this.dataset.parserUrl, location.href).href,
            buffer
          }, [buffer]);
        } catch (_) {
          finish(new Error('Excel reader unavailable. Try CSV or paste from Excel.'));
        }
      });
    }
    node(tag, text, cls) {
      const el = document.createElement(tag);
      if (text != null) el.textContent = text;
      if (cls) el.className = cls;
      return el;
    }
    render() {
      this.$('[data-review]').hidden = false;
      const container = this.$('[data-rows]');
      container.replaceChildren();
      const duplicates = new Map();
      this.rows.forEach(r => {
        const k = r.sku.toLowerCase();
        duplicates.set(k, (duplicates.get(k) || 0) + 1);
      });
      this.rows.forEach((row, index) => {
        const outer = this.node('div', null, 'dqo-row');
        const checkboxLabel = this.node('label', null, 'dqo-row-select');
        const checkbox = this.node('input');
        checkbox.type = 'checkbox';
        checkbox.dataset.rowSelect = String(index);
        checkbox.setAttribute('aria-label', 'Select row ' + (index + 1));
        checkbox.onchange = () => {
          row.checked = checkbox.checked;
          this.syncSelection();
        };
        checkboxLabel.append(checkbox, this.node('span', String(index + 1)));
        outer.append(checkboxLabel);
        [
          ['SKU', 'sku'],
          ['Quantity', 'quantity']
        ].forEach(([name, key]) => {
          const box = this.node('div'),
            label = this.node('label', name),
            input = this.node('input');
          input.type = 'text';
          input.value = row[key];
          input.setAttribute('aria-label', name + ' for row ' + (index + 1));
          input.autocomplete = 'off';
          input.placeholder = key==='sku'?'e.g. AF-01456':'1';
          if (key === 'quantity') input.inputMode = 'numeric';
          else input.maxLength = 200;
          label.append(input);
          box.append(label);
          outer.append(box);
          input.dataset.rowEdit = String(index);
          input.oninput = () => {
  row[key] = input.value.trim();
  if(row.sku)this.trackStarted();
  row.cartMessage = '';

  if (key === 'sku') {
    row.status = 'unchecked';
    row.message = 'SKU changed — check this row again.';
    row.matches = [];
    row.selected = null;
    row.checked = false;
  } else if (row.selected) {
    row.status = 'matched';
    row.message = validation(row) ||
      'Product matched. Stock and minimum quantity will be checked when adding to cart.';
  } else if (row.status === 'invalid') {
    row.status = 'unchecked';
    row.message = 'Quantity changed — check this SKU to continue.';
  }

  this.renderProduct(product, row);
  this.updateCount();
};
          if (key === 'sku' && row.sku && duplicates.get(row.sku.toLowerCase()) > 1) box
            .append(this.node('div', 'Repeated SKU — this row is kept separately.',
              'dqo-warning'));
        });
        const product = this.node('div', null, 'dqo-product');
        this.renderProduct(product, row);
        outer.append(product);
        const remove = this.node('button', 'Remove');
        remove.type = 'button';
        remove.dataset.remove = '';
        remove.setAttribute('aria-label', 'Remove row ' + (index + 1));
        remove.onclick = () => {
          this.rows.splice(index, 1);
          this.render();
        };
        outer.append(remove);
        container.append(outer);
      });
      this.updateCount();
      if (this.busy) this.setBusy(true);
    }
    updateCount() {
      const matched = this.rows.filter(r => r.status === 'matched').length;
      this.$('[data-count]').textContent = this.rows.length + ' rows · ' + matched +
        ' matched · ' + (this.rows.length - matched) + ' need checking or correction';
      this.syncSelection();
    }
    renderProduct(container, row) {
      container.replaceChildren();
      const content = this.node('div');
      const choice = row.selected;
      if (choice?.image_url) {
        try {
          const url = new URL(choice.image_url);
          if (url.protocol === 'https:') {
            const img = this.node('img');
            img.src = url.href;
            img.alt = '';
            img.loading = 'lazy';
            container.append(img);
          }
        } catch (_) {}
      }
      const result = this.node('div', row.message, 'dqo-result');
      result.dataset.state = row.status;
      content.append(result);
      if (row.cartMessage) content.append(this.node('div', row.cartMessage, row.cartState ===
        'added' ? 'dqo-success' : 'dqo-warning'));
      if (row.matches.length > 1) {
        const select = this.node('select');
        select.setAttribute('aria-label', 'Choose matching product for ' + row.sku);
        const placeholder = this.node('option', 'Choose the correct product / variant');
        placeholder.value = '';
        select.append(placeholder);
        row.matches.forEach(m => {
          const opt = this.node('option', m.product_title + ' — ' + m.variant_title +
            ' — ' + m.sku);
          opt.value = m.variant_id;
          select.append(opt);
        });
        select.value = choice?.variant_id || '';
        select.dataset.matchSelect = '';
        select.disabled = Boolean(row.cartState);
        select.onchange = () => {
          row.selected = row.matches.find(m => m.variant_id === select.value) || null;
          row.status = row.selected ? 'matched' : 'multiple_matches';
          row.message = row.selected ? 'Product selected — verify the image and variant.' :
            'Choose the correct product / variant.';
          row.checked = false;
          row.cartMessage = '';
          this.renderProduct(container, row);
          this.updateCount();
        };
        content.append(select);
      }
      if (choice) {
        const title = this.node('a', choice.product_title);
        const base = this.dataset.storeRoot || '/';
        title.href = base.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(choice
          .product_handle) + '?variant=' + encodeURIComponent(choice.variant_id);
        title.target = '_blank';
        title.rel = 'noopener';
        content.append(title);
        content.append(this.node('div', choice.variant_title === 'Default Title' ? 'SKU: ' +
          choice.sku : choice.variant_title + ' · ' + choice.sku, 'dqo-muted'));
      } else if (['not_found', 'multiple_matches'].includes(row.status)) {
        const link = this.node('a', 'Search products');
        link.href = (this.dataset.storeRoot || '/').replace(/\/?$/, '/') +
          'search?type=product&q=' + encodeURIComponent(row.sku);
        link.target = '_blank';
        link.rel = 'noopener';
        content.append(link);
      }
      container.append(content);
    }
    async check() {
      if (this.busy || !this.rows.length) return;
      if(!this.rows.some(r=>r.sku.trim())){this.say('Enter at least one SKU to match.');return;}
      this.rows=this.rows.filter(r=>r.sku.trim()||r.quantity!=='1'||r.cartState);
      this.controller = new AbortController();
      this.setBusy(true);
      this.$('[data-login]').hidden = true;
      const pending = [];
      this.rows.forEach(row => {
        if (row.cartState) return;
        const error = validation(row);
        if (error) {
          row.status = 'invalid';
          row.message = error;
          row.matches = [];
          row.selected = null;
        } else if (row.status !== 'matched') {
          row.status = 'unchecked';
          row.message = 'Waiting to check…';
          pending.push(row);
        }
      });
      this.render();
      let done = 0;
      try {
        for (let start = 0; start < pending.length; start += 10) {
          this.say('Checking items ' + (start + 1) + '–' + Math.min(start + 10, pending
            .length) + ' of ' + pending.length + '…');
          const batch = pending.slice(start, start + 10);
          const response = await this.resolveBatch(batch);
          if (!Array.isArray(response.rows) || response.rows.length !== batch.length)
          throw new Error('Incomplete match response. Check again.');
          response.rows.forEach((r, i) => {
            if (r.row !== i + 1 || clean(r.sku) !== batch[i].sku) throw new Error(
              'Unexpected match response. Check again.');
            if (!['matched', 'not_found', 'multiple_matches', 'invalid',
                'lookup_incomplete'].includes(r.status) || !Array.isArray(r.matches))
            throw new Error('Invalid match response.');
          });
          response.rows.forEach((r, i) => {
            Object.assign(batch[i], {
              status: r.status,
              message: r.message,
              matches: r.matches,
              selected: r.status === 'matched' && r.matches.length === 1 ? r.matches[
                0] : null,
              checked:r.status==='matched'&&r.matches.length===1&&!validation(batch[i])
            });
          });
          done += batch.length;
          this.render();
        }
        this.say(
          'Products matched. Unique matches are selected for you. Review images and variants, uncheck anything you do not want, then add to cart.'
          );
      } catch (error) {
  const loginRequired = !this.$('[data-login]').hidden;

  const message = loginRequired
    ? 'Please sign in, return to Quick Order, and check your items again.'
    : error.name === 'AbortError'
      ? 'Checking timed out or was stopped. Click Match items to retry.'
      : (error.message || 'Could not check items. Please try again.');

  pending.slice(done).forEach(row => {
    row.status = 'error';
    row.message = message;
    row.matches = [];
    row.selected = null;
    row.checked = false;
  });

  this.say(message);
  this.render();
} finally {
  this.controller = null;
  this.setBusy(false);
  this.updateCount();
}
    }
    storeUrl(path) {
      return (this.dataset.storeRoot || '/').replace(/\/?$/, '/') + path;
    }
    canSelect(row) {
      return row.status === 'matched' && row.selected && !row.cartState && !validation(row);
    }
    syncSelection() {
      if (!this.$) return;
      const eligible = this.rows.filter(row => this.canSelect(row));
      const selected = eligible.filter(row => row.checked);
      const all = this.$('[data-select-all]');
      all.disabled = this.busy || !eligible.length;
      all.checked = eligible.length > 0 && selected.length === eligible.length;
      all.indeterminate = selected.length > 0 && selected.length < eligible.length;
      this.$('[data-add-selected]').disabled = this.busy || !selected.length;
      this.$('[data-selected-count]').textContent = selected.length + ' selected';
      this.querySelectorAll('[data-row-select]').forEach(input => {
        const row = this.rows[Number(input.dataset.rowSelect)];
        input.disabled = this.busy || !this.canSelect(row);
        input.checked = Boolean(row.checked && this.canSelect(row));
      });
      this.querySelectorAll('[data-row-edit]').forEach(input => {
        input.disabled = this.busy || Boolean(this.rows[Number(input.dataset.rowEdit)]
          .cartState);
      });
      this.querySelectorAll('.dqo-row').forEach((el, index) => {
        const select = el.querySelector('[data-match-select]');
        if (select) select.disabled = this.busy || Boolean(this.rows[index].cartState);
      });
    }
    async getJSON(url, options = {}) {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 45000);
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          ...options,
          signal: controller.signal
        });
        let data;
        try {
          data = await response.json();
        } catch (_) {
          throw new Error('Could not read the store response. Please try again.');
        }
        if (!response.ok) throw new Error(typeof data.description === 'string' ? data
          .description : 'The store could not complete this request.');
        return data;
      } finally {
        clearTimeout(timer);
      }
    }
    cartQuantity(cart, id) {
      return cart.items.filter(item => String(item.variant_id || item.id) === String(id))
        .reduce((total, item) => total + Number(item.quantity), 0);
    }
    async validateGroup(group, cart) {
      const productUrl = new URL(this.storeUrl('products/' + encodeURIComponent(group.product
        .product_handle)), location.origin);
      productUrl.searchParams.set('variant', group.id);
      productUrl.searchParams.set('view', 'dluxca-quick-order-data');
      productUrl.searchParams.set('_dqo', String(Date.now()));
      const data = await this.getJSON(productUrl.toString());
      if (data.quick_order_version !== 2 || String(data.variant_id) !== group.id) {
        throw new Error(
          'Product validation is unavailable. Check that the Quick Order product template is installed.'
          );
      }
      if (clean(data.sku).toLowerCase() !== clean(group.product.sku).toLowerCase()) {
        throw new Error('This SKU has changed. Remove this row and check the SKU again.');
      }
      if (!data.available) throw new Error('Sold out or unavailable — not added.');
      if (data.requires_selling_plan || data.requires_components || data.gift_card) {
        throw new Error('Please open the product page to choose its purchase options.');
      }
      const rule = data.quantity_rule;
      if (!rule || !Number.isInteger(rule.min) || rule.min < 1 || !Number.isInteger(rule
          .increment) || rule.increment < 1 ||
        (rule.max !== null && (!Number.isInteger(rule.max) || rule.max < 1))) {
        throw new Error('Quantity rules could not be verified. Please open the product page.');
      }
      group.before = this.cartQuantity(cart, group.id);
      group.preorder = data.preorder === true;
      const minimum = Math.max(rule.min, group.preorder ? (data.preorder_moq || window.DluxcaAuditConfig.preorderMOQ) : 1);
      if (group.quantity < minimum) throw new Error((group.preorder ? 'Preorder' :
        'This item') + ' requires at least ' + minimum +
        '. Edit the quantity and check again.');
      if (group.quantity % rule.increment !== 0) throw new Error(
        'Use a quantity in multiples of ' + rule.increment + '.');
      if (rule.max !== null && group.before + group.quantity > rule.max) throw new Error(
        'Maximum ' + rule.max + ' allowed; ' + group.before + ' already in your cart.');
      if (data.tracked && !group.preorder) {
        if (!Number.isInteger(data.inventory_quantity)) throw new Error(
          'Stock could not be verified. Please open the product page.');
        const remaining = Math.max(0, data.inventory_quantity - group.before);
        if (group.quantity > remaining) throw new Error('Only ' + remaining +
          ' more available from current stock. Reduce the quantity, or open the product page for preorder options.'
          );
      }
      return group;
    }
    async addSelected() {
      if (this.busy) return;
      const selected = this.rows.filter(row => row.checked && this.canSelect(row));
      if (!selected.length) return;
      const grouped = new Map();
      selected.forEach(row => {
        const id = String(row.selected.variant_id);
        if (!grouped.has(id)) grouped.set(id, {
          id,
          product: row.selected,
          quantity: 0,
          rows: []
        });
        const group = grouped.get(id);
        group.quantity += Number(row.quantity);
        group.rows.push(row);
      });
      if (grouped.size > 50) {
        this.say(
          'Select up to 50 different variants per add. You can add the remaining rows afterwards.'
          );
        return;
      }
      if (!confirm('Add ' + selected.length + ' selected rows (' + [...grouped.values()].reduce(
            (sum, g) => sum + g.quantity, 0) +
          ' total quantity) to your cart? Repeated variants will be combined. Unavailable items or invalid quantities will be skipped.'
          )) return;
      this.setBusy(true);
      const valid = [];
      try {
        this.say('Checking stock and quantity rules…');
        const before = await this.getJSON(this.storeUrl('cart.js'));
        if (!Array.isArray(before.items)) throw new Error(
          'Could not read your cart. Nothing was added.');
        let checked = 0;
        for (const group of grouped.values()) {
          this.say('Checking availability ' + (++checked) + ' of ' + grouped.size + '…');
          try {
            await this.validateGroup(group, before);
            valid.push(group);
          } catch (error) {
            group.rows.forEach(row => {
              row.cartMessage = error.message;
            });
          }
        }
        this.render();
        if (!valid.length) {
          this.say('Nothing added. See the message beside each selected item.');
          return;
        }
        const preorders = valid.filter(group => group.preorder);
        if (preorders.length && !confirm(preorders.length +
            ' selected variants are preorders and will ship later. Continue adding these preorders and other eligible items?'
            )) {
          this.say('Cancelled. Nothing was added.');
          return;
        }
        this.say('Adding eligible items… Please wait.');
        const items = valid.map(group => ({
          id: group.id,
          quantity: group.quantity,
          ...(group.preorder ? {
            properties: {
              pre_order_item: 'Yes'
            }
          } : {})
        }));
        // A failed add may still change the cart. Never automatically retry a POST.
        let addError = '';
        try {
          const usageResult = await window.DluxcaAudit.addItems(valid.map(group => ({
            variant_id: group.id, quantity: group.quantity,
            product_handle: group.product.product_handle
          })));
          window.DluxcaUsage?.track('quick_order_cart',usageResult.failedItems?.length?'partial':'success');
        } catch (error) {
          window.DluxcaUsage?.track('quick_order_cart','error');
          addError = error.name === 'AbortError' ? 'The request timed out.' : error.message;
        }
        let after;
        try {
          after = await this.getJSON(this.storeUrl('cart.js'));
          if (!Array.isArray(after.items)) throw new Error();
        } catch (_) {
          valid.forEach(group => group.rows.forEach(row => {
            row.checked = false;
            row.cartState = 'uncertain';
            row.cartMessage =
              'Result not confirmed. Open View cart before adding this item again.';
          }));
          this.say(
            'The cart result could not be confirmed. Open View cart and check quantities; do not repeat this upload yet.'
            );
          return;
        }
        let added = 0,
          uncertain = 0;
        valid.forEach(group => {
          const change = this.cartQuantity(after, group.id) - group.before;
          const confirmed = change === group.quantity;
          if (confirmed) added += group.rows.length;
          else uncertain += group.rows.length;
          group.rows.forEach(row => {
            row.checked = false;
            row.cartState = confirmed ? 'added' : 'uncertain';
            row.cartMessage = confirmed ? 'Added to cart.' :
              'Cart quantity changed by ' + change + ' for this variant; requested ' +
              group.quantity + '. Open View cart before trying again.';
          });
        });
        this.say(added + ' rows added. ' + (selected.length - valid.reduce((n, g) => n + g.rows
            .length, 0)) + ' rows skipped.' +
          (uncertain ? ' ' + uncertain + ' rows need a cart check. ' + addError : '') +
          ' Open View cart to review prices and quantities.');
      } catch (error) {
        this.say(error.name === 'AbortError' ?
          'The store check timed out. Nothing was submitted to the cart.' : error.message);
      } finally {
        this.render();
        this.setBusy(false);
      }
    }
    async resolveBatch(batch) {
      const timer = setTimeout(() => this.controller?.abort(), 45000);
      try {
        const response = await fetch('/apps/invoices/quick-order/resolve', {
          method: 'POST',
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            rows: batch.map(r => ({
              sku: r.sku,
              quantity: r.quantity
            }))
          }),
          signal: this.controller.signal
        });
        let data;
        try {
          data = await response.json();
        } catch (_) {
          throw new Error('Unexpected response. Confirm the Quick Order Worker is deployed.');
        }
        if (!response.ok || !data.ok) {
          if (data.error?.code === 'LOGIN_REQUIRED') this.$('[data-login]').hidden = false;
          throw new Error(data.error?.message || 'Could not check items. Please retry.');
        }
        return data;
      } finally {
        clearTimeout(timer);
      }
    }
    downloadTemplate() {
      const url = URL.createObjectURL(new Blob(['\uFEFFSKU,Quantity\r\nAF-01456,10\r\n'], {
        type: 'text/csv;charset=utf-8'
      }));
      const a = this.node('a');
      a.href = url;
      a.download = 'DLUXCA-Quick-Order-Template.csv';
      document.body.append(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    }
  }
  if (!customElements.get('dluxca-quick-order')) customElements.define('dluxca-quick-order',
    QuickOrder);
})();
